/* eslint-disable max-len */
import React from 'react';
import { Typography } from '@vtblife/uikit';

import { Currency } from '@search/filter-enums/enums/Currency';
import { formatNumber } from '@search/helpers/src/formatNumber';
import { currencyMapping } from '@search/filter-enums/mappings/currency_mapping';
import {
    ApplicationIdEnum,
    MortgageProgramCodeType,
    MortgagePropertyType,
    MortgagePurpose
} from '@search/graphql-typings';
import { offerRegionToMortgage } from '@search/vtbeco-frontend-core/domain/offer/regionToBackoffice';
import {
    Mortgage2ConsultModal
} from '@search/vtbeco-frontend-core/view/common/components/MinimalBanksMortgage/mortgage2/consult/Mortgage2ConsultModal';
import type {
    Mortgage2RealtyType
} from '@search/vtbeco-frontend-core/view/common/components/MinimalBanksMortgage/mortgage2/mortgageTypes';
import {
    useClassifiedOfferDataLayerPush
} from '@search/vtbeco-frontend-core/domain/offer/analytics/ClassifiedOfferGtmContext';
import classname from '@search/classname/src';
import { RegionIdEnum } from '@search/filter-enums/enums/Region';
import { gtmShowMoreClick } from './components/common/constants';

import { useBanksMortgageOffers } from './components/common/BanksMortgageOffers/BanksMortgageOffers';
import { useMortgageAction } from './components/common/hooks/useMortgageAction';
import { MortgageNumberFields } from './components/common/MortgageNumberFields';
import { useBanksMortgageOffersForm } from './components/common/hooks/useBanksMortgageOffersForm';
import { useAdditionalActions } from './components/common/hooks/useAdditionalActions';
import { MortgageButtonsAction } from './components/common/MortgageButtonsAction';
import { EmptyResult } from './components/common/EmptyResult';
import { DesktopMortgagePrograms } from './components/desktop/DesktopMortgagePrograms';
import { MobileMortgagePrograms } from './components/mobile/MobileMortgageProgram';
import { DesktopMortgageOffers } from './components/desktop/DesktopMortgageOffers';
import { MobileMortgageOffers } from './components/mobile/MobileMortgageOffers';

import './ClMortgageCalculator.css';

const cn = classname.bind(null, 'ClMortgageCalculator');

interface IClDesktopMortgageCalculator {
    price: number;
    priceCurrency?: Currency;
    isNewBuilding?: boolean;
    regionId: number;
    realtyType: Mortgage2RealtyType;
    isMobile?: boolean;
    scrollToSimilar: () => void;
    changePayment?: (v: string) => void;
}

export const ClMortgageCalculator: React.FunctionComponent<IClDesktopMortgageCalculator> = React.memo(({
    price,
    priceCurrency = Currency.RUB,
    isNewBuilding = false,
    regionId,
    realtyType = 'ProfOfferFlat',
    isMobile,
    scrollToSimilar,
    changePayment,
}) => {
    const [ consultModal, setConsultModal ] = React.useState(false);

    const formattedPrice = `${formatNumber(price)}\u00A0${currencyMapping[priceCurrency]}`;

    const startRef = React.useRef<HTMLDivElement>(null);

    const correctedOwnAmount = (price - price * 0.3 > 6000000) ? (price - 6000000) : price * 0.3;

    const isBigLimit = React.useMemo(() => {
        return regionId === (RegionIdEnum.MSK_AND_MSK_OBL ||
            RegionIdEnum.SPB_AND_LEN_OBL ||
            RegionIdEnum.MSK ||
            RegionIdEnum.SPB ||
            RegionIdEnum.LEN_OBL ||
            RegionIdEnum.MSK_OBL
        );
    }, [ regionId ]);

    const {
        loading,
        mortgageFormData,
        setMortgageFormData,
        offersFull,
        isEmpty
    } = useBanksMortgageOffers({
        term: 20,
        propertyCost: price,
        ownAmount: isNewBuilding ? correctedOwnAmount : price * 0.2,
        purpose: isNewBuilding ? MortgagePurpose.Primary : MortgagePurpose.Secondary,
        regionId: offerRegionToMortgage(regionId),
        programCode: isNewBuilding ? MortgageProgramCodeType.Family : MortgageProgramCodeType.Base,
        propertyType: MortgagePropertyType.Flat,
    });

    const {
        formState,
        handleFieldChange,
        calculatedFormValues: {
            ownAmount,
            term
        }
    } = useBanksMortgageOffersForm({
        term: mortgageFormData.term,
        propertyCost: mortgageFormData.propertyCost,
        ownAmount: mortgageFormData.ownAmount,
        purpose: mortgageFormData.purpose,
        regionId: offerRegionToMortgage(regionId),
        programCode: mortgageFormData.programCode,
        propertyType: mortgageFormData.propertyType,
        onChange: setMortgageFormData,
        isBigLimit
    });

    const { goToMortgageApplication } = useMortgageAction({
        term: mortgageFormData.term,
        propertyCost: mortgageFormData.propertyCost,
        ownAmount: mortgageFormData.ownAmount,
        purpose: mortgageFormData.purpose,
        regionId: offerRegionToMortgage(regionId),
    });

    const dataLayerPush = useClassifiedOfferDataLayerPush();

    React.useEffect(() => {
        if (offersFull[0]?.minimalPaymentFormatted && changePayment) changePayment(offersFull[0].minimalPaymentFormatted);
    }, [ changePayment, offersFull ]);

    React.useEffect(() => {
        const observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    dataLayerPush({ event: 'classified_card_sale_ipoteka_calc_show' });
                }
            });
        }, {
            threshold: [ 0, 0.1 ]
        });

        observer.observe(startRef.current as HTMLDivElement);

        return () => {
            observer.disconnect();
        };
    }, [ dataLayerPush ]);

    const handleProgramChange = React.useCallback((value: string) => {
        handleFieldChange('programCode', value);
    }, [ handleFieldChange ]);

    const consultModalOpen = React.useCallback((e?: React.MouseEvent<Element, MouseEvent>) => {
        e?.preventDefault();
        dataLayerPush({ event: gtmShowMoreClick });
        setConsultModal(true);
    }, [ dataLayerPush ]);

    const consultModalClose = React.useCallback(() => {
        setConsultModal(false);
    }, [ ]);

    const { reset, goBaseProgram } = useAdditionalActions({
        // @ts-ignore
        fieldsChange: handleFieldChange,
        price
    });

    const scrollToStart = React.useCallback(() => {
        startRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    const desktopMarkup = (
        <section className={cn('desktopWrapper')} ref={startRef}>
            <Typography variant='h2' bold>
                Ипотека от М2
            </Typography>
            {isNewBuilding ? <DesktopMortgagePrograms
                stateProgramCode={formState.programCode}
                // @ts-ignore
                onChange={handleProgramChange}
            /> : undefined}
            <MortgageNumberFields
                formattedPrice={formattedPrice}
                term={term}
                ownAmount={ownAmount}
                formState={formState}
                handleOwnAmountChange={handleFieldChange.bind(null, 'ownAmount')}
                handleTermChange={handleFieldChange.bind(null, 'term')}
            />
            {! isEmpty ? (
                <DesktopMortgageOffers
                    offers={offersFull}
                    loading={loading}
                    handleClick={goToMortgageApplication}
                    scrollToStart={scrollToStart}
                />
            ) : (
                <EmptyResult
                    reset={reset}
                    goBaseProgram={goBaseProgram}
                    shortBtns={formState.programCode === 'BASE'}
                    scrollToSimilar={scrollToSimilar}
                />
            )}
            <MortgageButtonsAction
                loading={loading}
                isEmpty={isEmpty}
                openConsultModal={consultModalOpen}
                linkAction={goToMortgageApplication}
            />
            <Typography variant='small-alone' color='black-350'>
                Расчёт условий не&nbsp;является публичной офертой. Финальные условия кредитования определяются при заключении договора
            </Typography>
            {consultModal ? <Mortgage2ConsultModal
                regionId={regionId}
                realtyType={realtyType}
                applicationId={ApplicationIdEnum.ClassifiedDesktop}
                locationPlacement='калькулятор'
                onClose={consultModalClose}
            /> : null}
        </section>
    );

    const mobileMarkup = (
        <section
            ref={startRef}
            className={cn('mobileWrapper')}
        >
            <Typography variant='h3' bold>
                Ипотека от М2
            </Typography>
            {isNewBuilding ? <MobileMortgagePrograms
                stateProgramCode={formState.programCode}
                // @ts-ignore
                onChange={handleProgramChange}
            /> : undefined}
            <MortgageNumberFields
                formattedPrice={formattedPrice}
                term={term}
                ownAmount={ownAmount}
                formState={formState}
                handleOwnAmountChange={handleFieldChange.bind(null, 'ownAmount')}
                handleTermChange={handleFieldChange.bind(null, 'term')}
                isMobile
            />
            {! isEmpty ? (
                <MobileMortgageOffers
                    offers={offersFull}
                    loading={loading}
                    handleClick={goToMortgageApplication}
                    scrollToStart={scrollToStart}
                />
            ) : (
                <EmptyResult
                    reset={reset}
                    goBaseProgram={goBaseProgram}
                    shortBtns={formState.programCode === 'BASE'}
                    isMobile
                    scrollToSimilar={scrollToSimilar}
                />
            )}
            <MortgageButtonsAction
                loading={loading}
                isEmpty={isEmpty}
                openConsultModal={consultModalOpen}
                linkAction={goToMortgageApplication}
                isMobile
            />
            <Typography variant='small-alone' color='black-300-alpha'>
                Расчёт условий не&nbsp;является публичной офертой. Финальные условия кредитования определяются при
                заключении договора
            </Typography>
            {consultModal ? <Mortgage2ConsultModal
                regionId={regionId}
                realtyType={realtyType}
                applicationId={ApplicationIdEnum.ClassifiedTouch}
                locationPlacement='калькулятор'
                onClose={consultModalClose}
            /> : null}
        </section>);

    return isMobile ? mobileMarkup : desktopMarkup;
});
